import {
  Box,
  Button,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  TextField,
} from '@mui/material';
import React from 'react';
import { defineMessages, useIntl } from 'react-intl';

import DateInput from '@components/DateInput/DateInput';

import type {
  DiscernClientFilters,
  DiscernServerFilters,
} from '@hooks/useDiscernFilters';

import { CurationStatus } from 'generated/types';

const DISCERN_STATUSES = [
  CurationStatus.New,
  CurationStatus.Preparation,
  CurationStatus.Structuring,
  CurationStatus.InReview,
  CurationStatus.Failed,
  CurationStatus.Complete,
] as const;

const messages = defineMessages({
  dateFrom: {
    id: 'curationFilters.dateFrom',
    defaultMessage: 'Upload Date From',
  },
  dateTo: {
    id: 'curationFilters.dateTo',
    defaultMessage: 'Upload Date To',
  },
  status: {
    id: 'curationFilters.status',
    defaultMessage: 'Status',
  },
  patientName: {
    id: 'curationFilters.patientName',
    defaultMessage: 'Patient Name',
  },
  uploader: {
    id: 'curationFilters.uploader',
    defaultMessage: 'Uploaded By',
  },
  clearAll: {
    id: 'curationFilters.clearAll',
    defaultMessage: 'Clear All',
  },
  allStatuses: {
    id: 'curationFilters.allStatuses',
    defaultMessage: 'All Statuses',
  },
  allUploaders: {
    id: 'curationFilters.allUploaders',
    defaultMessage: 'All Uploaders',
  },
  statusNew: {
    id: 'curationFilters.statusNew',
    defaultMessage: 'New',
  },
  statusPreparation: {
    id: 'curationFilters.statusPreparation',
    defaultMessage: 'Preparation',
  },
  statusStructuring: {
    id: 'curationFilters.statusStructuring',
    defaultMessage: 'Structuring',
  },
  statusInReview: {
    id: 'curationFilters.statusInReview',
    defaultMessage: 'In Review',
  },
  statusFailed: {
    id: 'curationFilters.statusFailed',
    defaultMessage: 'Failed',
  },
  statusComplete: {
    id: 'curationFilters.statusComplete',
    defaultMessage: 'Complete',
  },
});

const STATUS_LABELS = {
  [CurationStatus.New]: messages.statusNew,
  [CurationStatus.Preparation]: messages.statusPreparation,
  [CurationStatus.Structuring]: messages.statusStructuring,
  [CurationStatus.InReview]: messages.statusInReview,
  [CurationStatus.Failed]: messages.statusFailed,
  [CurationStatus.Complete]: messages.statusComplete,
};

interface CurationFiltersProps {
  serverFilters: DiscernServerFilters;
  clientFilters: DiscernClientFilters;
  onServerFilterChange: <K extends keyof DiscernServerFilters>(
    key: K,
    value: DiscernServerFilters[K]
  ) => void;
  onClientFilterChange: <K extends keyof DiscernClientFilters>(
    key: K,
    value: DiscernClientFilters[K]
  ) => void;
  onClearAll: () => void;
  hasActiveFilters: boolean;
  availableUploaders?: string[];
  showPatientSearch?: boolean;
}

const DEBOUNCE_MS = 500;

const CurationFilters: React.FC<CurationFiltersProps> = ({
  serverFilters,
  clientFilters,
  onServerFilterChange,
  onClientFilterChange,
  onClearAll,
  hasActiveFilters,
  availableUploaders = [],
  showPatientSearch = true,
}) => {
  const { formatMessage } = useIntl();
  const [patientNameInput, setPatientNameInput] = React.useState(
    serverFilters.patientName
  );

  React.useEffect(() => {
    const timer = setTimeout(() => {
      onServerFilterChange('patientName', patientNameInput);
    }, DEBOUNCE_MS);
    return () => clearTimeout(timer);
  }, [patientNameInput, onServerFilterChange]);

  React.useEffect(() => {
    setPatientNameInput(serverFilters.patientName);
  }, [serverFilters.patientName]);

  return (
    <Box display="flex" gap={2} flexWrap="wrap" alignItems="flex-end" mb={2}>
      <DateInput
        label={formatMessage(messages.dateFrom)}
        value={serverFilters.dateFrom}
        onChange={(date) => onServerFilterChange('dateFrom', date)}
        maxDate={serverFilters.dateTo ?? undefined}
      />

      <DateInput
        label={formatMessage(messages.dateTo)}
        value={serverFilters.dateTo}
        onChange={(date) => onServerFilterChange('dateTo', date)}
        minDate={serverFilters.dateFrom ?? undefined}
      />

      <FormControl sx={{ minWidth: 160 }} size="small">
        <InputLabel>{formatMessage(messages.status)}</InputLabel>
        <Select
          label={formatMessage(messages.status)}
          value={clientFilters.status}
          onChange={(e) => onClientFilterChange('status', e.target.value)}
        >
          <MenuItem value="">{formatMessage(messages.allStatuses)}</MenuItem>
          {DISCERN_STATUSES.map((s) => (
            <MenuItem key={s} value={s}>
              {formatMessage(STATUS_LABELS[s])}
            </MenuItem>
          ))}
        </Select>
      </FormControl>

      {showPatientSearch && (
        <TextField
          label={formatMessage(messages.patientName)}
          size="small"
          value={patientNameInput}
          onChange={(e) => setPatientNameInput(e.target.value)}
          sx={{ minWidth: 180 }}
        />
      )}

      {availableUploaders.length > 0 && (
        <FormControl sx={{ minWidth: 180 }} size="small">
          <InputLabel>{formatMessage(messages.uploader)}</InputLabel>
          <Select
            label={formatMessage(messages.uploader)}
            value={clientFilters.uploader}
            onChange={(e) => onClientFilterChange('uploader', e.target.value)}
          >
            <MenuItem value="">{formatMessage(messages.allUploaders)}</MenuItem>
            {availableUploaders.map((name) => (
              <MenuItem key={name} value={name}>
                {name}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      )}

      {hasActiveFilters && (
        <Button variant="outlined" size="small" onClick={onClearAll}>
          {formatMessage(messages.clearAll)}
        </Button>
      )}
    </Box>
  );
};

export default CurationFilters;
